<template>
	<div class="profile-image-selector-wrapper">
		<div class="uploader-input-container">
			<div class="image-wrapper">
				<!-- Image placeholder -->
				<img v-show="!imageIsLoaded" class="default-image" :src="imagePlaceholder"/>
				<!-- User image -->
				<img v-show="imageIsLoaded" class="selected-image" :src="ourImage" @error="imageErrorHandler" @load="imageLoadHandler"/>
			</div>
		</div>
		<div class="uploader-buttons-container">
			<div class="buttons-section">
				<button-secondary
				v-if="!imageIsLoaded"
				class="upload-image-button"
				icon="add-photo"
				:label="t('uploader.select')"
				@clicked="showUploadModal = true"/>
				<button-secondary
				v-if="imageIsLoaded"
				icon="cycle"
				:label="t('uploader.replace')"
				@clicked="showUploadModal = true"/>
				<button-secondary
				v-if="imageIsLoaded"
				class="delete-button"
				hasError
				icon="delete"
				@clicked="showDeleteModal = true"/>
			</div>
			<div class="informative-section">
				<p class="types">{{t('uploader.typeImageMessage')}}</p>
				<p class="sizes">{{t('uploader.sizeImageMessage')}}</p>
			</div>
		</div>
	</div>
    <!-- Upload Modal -->
    <upload-modal-mobile
    v-if="showUploadModal"
    :imageUrl="ourImage"
    :userImageData="userImageData"
    :triggerClose="triggerCloseUploadModal"
    @openCamera="triggerCameraHandler(true)"
    @openGallery="triggerCameraHandler(false)"
    @handleDelete="showDeleteModal = true"
    @saveImage="saveImageChangeHandler"
    @hideMe="[closeModalHandler(), triggerCloseUploadModal = false]"/>
    <!-- Delete Image Modal -->
    <delete-image-modal
    v-if="showDeleteModal"
    @rightButtonClicked="handleDelete"
    @hideMe="showDeleteModal = false"/>
	<!-- Feedback -->
    <toast-generic
    v-if="showFeedbackToast"
    :toastType="feedbackToastData.toastType"
    :feedbackText="feedbackToastData.text"
    :buttonsParams="feedbackToastData.buttons"
    @hideMe="showFeedbackToast = false"/>
	<!-- Loader -->
	<loader-generic v-if="showOverlaySpinner" isOverlay/>
</template>

<script setup>
import ButtonSecondary from '@/components/generic/buttons/Secondary'
import ToastGeneric from '@/components/generic/toasts/Generic'
import UploadModalMobile from '@/components/generic/uploader/profileImageUploader/UploadModalMobile'
import DeleteImageModal from '@/components/generic/uploader/profileImageUploader/DeleteImageModal'
import LoaderGeneric from '@/components/generic/loaders/Generic'

/* Vue */
import { ref, computed } from 'vue'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { requestCustomerImageUrl } from '@/network/api/customers/customers.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const emit = defineEmits(['hasProfilePicture'])

/* mapActions & mapGetters */
const { updateCustomerImage, deleteCustomerImage } = mapActions(moduleMapper.USER_AREA_PROFILE)
const { updateCustomerImageUrl } = mapActions(moduleMapper.SESSION)
const { getAllCustomerInfo } = mapGetters(moduleMapper.SESSION)
const { getAppThemeMode } = mapGetters()

/* We need to check if our base64Image string has the correct data type, if it doesnt we set it to the right one */
const base64ImageParse = () => {
	const imageUrl = process.env.VUE_APP_ENV === 'QUA' ? requestCustomerImageUrl() : getAllCustomerInfo.value?.customer.avatarImageUrl

	if (imageUrl.search('data:application/json;')) return imageUrl.replace('data:application/json;', 'data:image/jpeg;')

	return imageUrl
}

/* DATA */
const ourImage = ref(base64ImageParse())
const triedFetchingImg = ref(false)
const triggerCloseUploadModal = ref(false)

const showOverlaySpinner = ref(false)

const imageIsLoaded = ref(false)

const showUploadModal = ref(false)

const showDeleteModal = ref(false)

const userImageData = ref({
	imageUrl: process.env.VUE_APP_ENV === 'QUA' ? requestCustomerImageUrl() : getAllCustomerInfo.value?.customer.avatarImageUrl,
	type: '',
	imageName: '',
	imageBinaryBlob: null
})

const imagePlaceholder = computed(() => {
    return getAppThemeMode.value === 'dark' ? './images/user_image_placeholder_dark.svg' : './images/user_image_placeholder.svg'
})
/* Triggers Camera/PhotoLibrary to open */

const triggerCameraHandler = openCamera => {
	const cameraOptions = {
		correctOrientation: true,
		sourceType: openCamera ? navigator.camera.PictureSourceType.CAMERA : navigator.camera.PictureSourceType.PHOTOLIBRARY,
		cameraDirection: navigator.camera.Direction.FRONT,
		quality: 100
	}

	navigator.camera.getPicture(cameraSuccess, cameraError, cameraOptions)
}

/** Receives URI type file and passes it on to readBinaryFile */

const getFileEntry = imgUri => {
	window.resolveLocalFileSystemURL(imgUri, fileEntry => { readBinaryFile(fileEntry) }, resolveLocalFileSystemURLErrorHandler)
}

/* Controls if the file extension is valid or invalid and shows error toast if invalid */
const resolveLocalFileSystemURLErrorHandler = error => {
	if (error.code === 5 || error.code === 11) {
		showFeedbackToast.value = true
		feedbackToastData.value = {
			toastType: 'negative',
			text: t('uploader.errorToast.invalidFileFormatText')
		}
	}
}

/* Receives URI type file and transforms it to image object file */
const readBinaryFile = fileEntry => {
	fileEntry.file(file => {
		var reader = new FileReader()
		reader.onloadend = () => {
			var blob = new Blob([new Uint8Array(file)], { type: 'image/png' })
		}

		reader.readAsArrayBuffer(file)

		createImage(file)
	})
}

const convertDataURIToBinary = dataURI => {
	var BASE64_MARKER = ';base64,'
	var base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length
	var base64 = dataURI.substring(base64Index)
	var raw = window.atob(base64)
	var rawLength = raw.length
	var array = new Uint8Array(new ArrayBuffer(rawLength))
	for (var i = 0; i < rawLength; i++) {
		array[i] = raw.charCodeAt(i)
	}
	return array
}

/** creates image blob and propagates it
@file { object } - event information
@return { void }
*/
const createImage = (file) => {
    const reader = new FileReader()

    reader.onload = event => {
		var base64Img = event.target.result // gets the image in base 64
		var binaryImg = convertDataURIToBinary(base64Img) // converts base 64 to binary
		var blob = new Blob([binaryImg], { type: file.type }) // creates the blob with the binary
		var fileName = file.name // get the name of tile

		userImageData.value.imageUrl = base64Img
		userImageData.value.imageName = fileName
		userImageData.value.imageBinaryBlob = blob
    }

    reader.readAsDataURL(file)
}

/** validates if image is loaded
@return { void }
*/
const imageLoadHandler = () => {
    imageIsLoaded.value = true
}

/** hangles image load error
@return { void }
*/
const imageErrorHandler = () => {
    console.log(ourImage.value, 'imageLoadError')
    if (!triedFetchingImg.value) {
        triedFetchingImg.value = true
        if (process.env.VUE_APP_ENV === 'QUA') {
            ourImage.value = requestCustomerImageUrl()
            return
        }
    }
	imageIsLoaded.value = false
}

const closeModalHandler = () => {
	userImageData.value = {
		imageUrl: process.env.VUE_APP_ENV === 'QUA' ? requestCustomerImageUrl() : getAllCustomerInfo.value?.customer.avatarImageUrl,
		type: '',
		imageName: '',
		imageBinaryBlob: null
	}
	showUploadModal.value = false
}

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const saveImageChangeHandler = async () => {
    showOverlaySpinner.value = true

	window.cordova.plugin.http.setDataSerializer('multipart')

	const formData = new FormData()

	formData.append('file', userImageData.value.imageBinaryBlob, userImageData.value.imageName)
    try {
        const response = await updateCustomerImage(formData)
        triedFetchingImg.value = false
		ourImage.value = response ? response.data.data.avatarImageUrl : ''

        successHandler(response.data)
		updateCustomerImageUrl(response.data.data.avatarImageUrl)
		userImageData.value.imageUrl = response.data.data.avatarImageUrl

        triggerCloseUploadModal.value = true
		emit('hasProfilePicture', true)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error.response ? error.response.data : error, saveImageChangeHandler)
	}
}

const handleDelete = async () => {
	cancelAllPendingRequests()
	showDeleteModal.value = false
	showOverlaySpinner.value = true
	try {
        const res = await deleteCustomerImage()
		ourImage.value = ''
		updateCustomerImageUrl('')
        successHandler(res.data)

		emit('hasProfilePicture', false)
	} catch (error) {
		if (error.toString() === 'Cancel') return
		errorHandler(error)
	}
}

/*************************************************
*                                                *
*                 Toast Handler                  *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref({
	toastType: '',
	text: '',
	buttons: []
})

const successHandler = response => {
	window.cordova.plugin.http.setDataSerializer('json')

	feedbackToastData.value = {
		toastType: 'success',
		text: response.message,
		buttons: []
	}

	showFeedbackToast.value = true
	showOverlaySpinner.value = false
}

const cameraSuccess = imageData => {
	getFileEntry(imageData)
	showUploadModal.value = true
}

const errorHandler = (data, retryFn) => {
	window.cordova.plugin.http.setDataSerializer('json')

	feedbackToastData.value = {
        toastType: data.errorLevel ? data.errorLevel.toLowerCase() : 'error',
		text: data && data.message ? data.message : t('genericErrorMessage'),
		buttons: [
            {
				text: t('uploader.errorToast.ignore'),
				callbackAction: () => { showFeedbackToast.value = false }
			},
			{
                text: t('uploader.errorToast.tryAgain'),
				callbackAction: retryFn
			}
		]
	}

	showFeedbackToast.value = true
	showOverlaySpinner.value = false
}

const cameraError = message => { cameraErrorHandler(message) }

const cameraErrorHandler = message => {
	if (message === 'No Image Selected') return

	feedbackToastData.value = {
        toastType: 'error',
		text: message || t('genericErrorMessage'),
		buttons: []
	}

	showFeedbackToast.value = true
}
</script>

<style lang="scss" scoped>
.profile-image-selector-wrapper {
	width: 100%;
	height: 170px;
	margin-bottom: 32px;

	position: relative;
	display: flex;

	.uploader-input-container {
		display: flex;
		flex-direction: row;
		align-items: center;
		justify-content: center;

		.image-wrapper {
			width: 120px;
			height: 120px;

			margin-right: 16px;
			border-radius: 50%;

			overflow: hidden;

			.selected-image, .default-image {
				width: 120px;
				height: 120px;
				border-radius: 50%;

				object-fit: cover;
			}
		}
	}

	.uploader-buttons-container {
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: center;

		margin-top: 16px;

		.buttons-section {
			max-width: 200px;
			display: flex;
			flex-direction: row;
			justify-content: center;
			align-items: center;

			margin-bottom: 16px;
			gap: 8px;

			.upload-image-button {
				min-width: 125px;
			}

			.delete-button {
                :deep(.change-color-fill) {
                    fill: var(--negative);
                }
            }
		}

		.informative-section {
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
			align-items: flex-start;
			align-self: flex-start;

			gap: 2px;
			pointer-events: none;

			.types, .sizes {
				@include H9-Regular;
				color: var(--secondary-dark-3);
			}
		}
	}

	.overlay-wrapper {
		width: 390px;
		height: 280px;
		padding-top: 25px;

		position: absolute;
		z-index: 1;
		left: 0px;
		top: 0px;
		box-sizing: border-box;

		display:flex;
		flex-direction:column;

		background-color:var(--white);
		box-shadow: 0 10px 16px 0 rgba(0, 50, 90, 0.24);

		.change-photo-title {
			height: 24px;
			width: 100%;
			text-align: center;
			font-size: 21px;
			font-weight: 500;
			letter-spacing: normal;
			color: var(--primary);
			display: block;
		}
		.overlay-body {
			height: 222px;
			padding-left: 16px;

			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: center;
			box-sizing: border-box;

			.image-wrapper {
				width: 120px;
				height: 120px;
				margin-right: 16px;
				border-radius: 50%;

				line-height: 120px;
				text-align: center;

				overflow: hidden;

				.selected-image, .default-image {
					width: 120px;
					height: 120px;

					border-radius: 50%;

					object-fit: cover;
				}
			}

			.uploader-buttons-container {
				display: flex;
				flex-direction: column;
				justify-content: center;
				align-items: center;

				.buttons-section {
					display: flex;
					flex-direction: row;
					justify-content: center;
					align-items: center;
					margin-bottom: 16px;
					gap: 16px;

					:deep(.button-raised) {
						min-width: unset;
					}
				}

				.informative-section {
					display: flex;
					flex-direction: column;
					justify-content: flex-start;
					align-items: flex-start;
					align-self: flex-start;

					gap: 6px;
					pointer-events: none;

					.types, .sizes {
						margin: 0;
						padding: 0;

						font-size: 12px;
						font-weight: 500;
						color: var(--secondary-dark-3);
					}
				}
			}
		}

		.overlay-footer {
			height: 56px;

			display: flex;
			flex-direction: row;
			align-items: center;
			justify-content: space-around;

			border-top: 1px solid  var(--secondary-variant-5);

			:deep(.button-raised) {
				width: 100%;
				min-width: unset;
				height: 56px;

				box-shadow:none;
			}

			.save-image-button {
				:deep(.text) {
					color:var(--primary);
				}
			}

		}
	}
}

.appear-enter, .appear-leave-to {
	opacity: 0;
}
.appear-enter-active, .appear-leave-active {
	transition:all 0.3s linear;
}
</style>
